import type { ArrayBufferRc, ReadonlyUint8Array, ReadonlyInt16Array, ReadonlyFloat32Array, ReadonlyUint32Array } from '../types/bufferRc';
import type { Logger } from '../types/logger';

export interface BufferCacheStats {
  created: number,
  free: number,
}

export interface BufferCache {
  allocateUint8Array(count: number, mutate: (uint8Array: Uint8Array) => void): ArrayBufferRc;
  allocateInt16Array(count: number, mutate: (int16Array: Int16Array) => void): ArrayBufferRc;
  allocateFloat32Array(count: number, mutate: (float32Array: Float32Array) => void): ArrayBufferRc;
  allocateUint32Array(count: number, mutate: (uint32Array: Uint32Array) => void): ArrayBufferRc;
  stats(): BufferCacheStats;
}

export function createBufferCache(logger: Logger): BufferCache {
  return new BufferCacheInternal(logger);
}

// ---


interface InternalBuffer {
  // These are all Typed views into the same underlying ArrayBuffer
  uint8Array : Uint8Array,
  // We cache these additional views into the buffer in order to
  // avoid putting unnecessary load on the garbage collector.
  float32Array: Float32Array | null,
  int16Array: Int16Array | null,
  uint32Array: Uint32Array | null,
  refCount : number,
  bufferCache: BufferCacheInternal,
}

function ensureInt16Array(internal: InternalBuffer): Int16Array {
  if (internal.int16Array === null) {
    internal.int16Array = new Int16Array(internal.uint8Array.buffer, internal.uint8Array.byteOffset);
  }
  return internal.int16Array;
}

function ensureFloat32Array(internal: InternalBuffer): Float32Array {
  if (internal.float32Array === null) {
    internal.float32Array = new Float32Array(internal.uint8Array.buffer, internal.uint8Array.byteOffset);
  }
  return internal.float32Array;
}

function ensureUint32Array(internal: InternalBuffer): Uint32Array {
  if (internal.uint32Array === null) {
    internal.uint32Array = new Uint32Array(internal.uint8Array.buffer, internal.uint8Array.byteOffset);
  }
  return internal.uint32Array;
}

interface AllocInfo {
  wasClosed: boolean,
  stack: string | undefined,
}

class BufferCacheRcBuffer implements ArrayBufferRc {
  private internal: InternalBuffer | null = null;
  private start: number = 0;
  private end: number = 0;
  private allocInfo: AllocInfo | null = null;

  repurpose(
    internal: InternalBuffer,
    start: number,
    end: number,
    allocInfo: AllocInfo | null,
  ) {
    this.internal = internal;
    this.internal.refCount += 1;
    this.start = start;
    this.end = end;
    this.allocInfo = allocInfo;
  }

  constructor() { }

  size(): number {
    if (this.internal === null) {
      throw new Error('Buffer is closed!');
    }
    return this.end - this.start;
  }

  close(): void {
    if (this.internal === null) {
      throw new Error('Buffer is closed!');
    }
    this.internal.refCount -= 1;
    if (this.internal.refCount < 0) {
      throw new Error('Buffer ref count underflow');
    }
    if (this.internal.refCount === 0) {
      this.internal.bufferCache.returnInternalBuffer(this.internal);
    }
    if (this.allocInfo !== null) {
      this.allocInfo.wasClosed = true;
    }
    this.internal.bufferCache.returnBufferRc(this);
    this.internal = null;
  }

  asReadonlyUint8Array(): ReadonlyUint8Array {
    if (this.internal === null) {
      throw new Error('Buffer is closed!');
    }
    if (this.start === 0 && this.end === this.internal.uint8Array.length) {
      return this.internal.uint8Array;
    } else {
      return this.internal.uint8Array.subarray(this.start, this.end);
    }
  }

  asReadonlyInt16Array(): ReadonlyInt16Array {
    if (this.internal === null) {
      throw new Error('Buffer is closed!');
    }
    if (this.start === 0 && this.end === this.internal.uint8Array.length) {
      return ensureInt16Array(this.internal);
    } else {
      return new Int16Array(this.internal.uint8Array.buffer, this.start, Math.floor((this.end - this.start) / 2));
    }
  }

  asReadonlyFloat32Array(): ReadonlyFloat32Array {
    if (this.internal === null) {
      throw new Error('Buffer is closed!');
    }
    if (this.start === 0 && this.end === this.internal.uint8Array.length) {
      return ensureFloat32Array(this.internal);
    } else {
      return new Float32Array(this.internal.uint8Array.buffer, this.start, Math.floor((this.end - this.start) / 4));
    }
  }

  asReadonlyUint32Array(): ReadonlyUint32Array {
    if (this.internal === null) {
      throw new Error('Buffer is closed!');
    }
    if (this.start === 0 && this.end === this.internal.uint8Array.length) {
      return ensureUint32Array(this.internal);
    } else {
      return new Uint32Array(this.internal.uint8Array.buffer, this.start, Math.floor((this.end - this.start) / 4));
    }
  }

  addReference(): ArrayBufferRc {
    if (this.internal === null) {
      throw new Error('Buffer is closed!');
    }
    return this.internal.bufferCache.getBufferRc(this.internal, this.start, this.end);
  }

  subarray(start: number, end?: number): ArrayBufferRc {
    if (this.internal === null) {
      throw new Error('Buffer is closed!');
    }
    const totalStart = this.start + start;
    const totalEnd = (end === undefined) ? this.end : (this.start + end);
    return this.internal.bufferCache.getBufferRc(this.internal, totalStart, totalEnd);
  }

  toArrayBuffer(): ArrayBufferRc {
    return this.addReference();
  }
}

class BufferCacheInternal implements BufferCache {
  private internalBuffers : Record<number, InternalBuffer[]> = { };
  private finalizationRegistry : FinalizationRegistry<AllocInfo> | null = null;
  private bufferRcs : BufferCacheRcBuffer[] = [];
  private _stats : BufferCacheStats = {
    created: 0,
    free: 0,
  };
  private createdWarningThreshold : number = 300;
  private allocInfos : AllocInfo[] = [];
  constructor(private logger: Logger, bufferTracking: boolean = false) {
    if (bufferTracking) {
      this.finalizationRegistry = new FinalizationRegistry<AllocInfo>((allocInfo: AllocInfo) => {
        if (!allocInfo.wasClosed) {
          logger.warn(`ArrayBuffer allocated at:\n${allocInfo.stack}\n was not properly closed`);
        }
        allocInfo.wasClosed = true;
        allocInfo.stack = undefined;
        this.allocInfos.push(allocInfo);
      });
    }
  }

  public returnInternalBuffer(internalBuffer: InternalBuffer) {
    this.internalBuffers[internalBuffer.uint8Array.length]!.push(internalBuffer);
    this._stats.free++;
  }

  public returnBufferRc(bufferRc: BufferCacheRcBuffer) {
    this.bufferRcs.push(bufferRc);
  }

  public allocateUint8Array(count: number, mutate: (uint8Array: Uint8Array) => void): ArrayBufferRc {
    return this.allocateBuffer(count, (internalBuffer) => mutate(internalBuffer.uint8Array));
  }

  public allocateInt16Array(count: number, mutate: (int16Array: Int16Array) => void): ArrayBufferRc {
    return this.allocateBuffer(count * 2, (internalBuffer) => mutate(ensureInt16Array(internalBuffer)));
  }

  public allocateFloat32Array(count: number, mutate: (float32Array: Float32Array) => void): ArrayBufferRc {
    return this.allocateBuffer(count * 4, (internalBuffer) => mutate(ensureFloat32Array(internalBuffer)));
  }

  public allocateUint32Array(count: number, mutate: (uint32Array: Uint32Array) => void): ArrayBufferRc {
    return this.allocateBuffer(count * 4, (internalBuffer) => mutate(ensureUint32Array(internalBuffer)));
  }


  private allocateBuffer(size: number, mutate: (internalBuffer: InternalBuffer) => void): ArrayBufferRc {
    this.internalBuffers[size] ??= [];
    const cache = this.internalBuffers[size];
    let internalBuffer = cache.pop();
    if (internalBuffer === undefined) {
      this._stats.created++;
      if (this._stats.created > this.createdWarningThreshold) {
        this.logger.warn(`Warning threshold for created buffers reached at ${this.createdWarningThreshold}. If you keep seeing this warning we are most likely leaking buffers.`);
        this.createdWarningThreshold *= 2;
      }
      const arrayBuffer = new ArrayBuffer(size);
      internalBuffer = {
        uint8Array: new Uint8Array(arrayBuffer),
        int16Array: null,
        float32Array: null,
        uint32Array: null,
        refCount: 0,
        bufferCache: this,
      };
    } else {
      this._stats.free--;
    }
    mutate(internalBuffer);

    return this.getBufferRc(internalBuffer, 0, size);
  }

  public getBufferRc(
    internalBuffer: InternalBuffer,
    start: number,
    end: number,
  ): BufferCacheRcBuffer {
    let bufferRc = this.bufferRcs.pop() ?? new BufferCacheRcBuffer();
    let allocInfo : AllocInfo | null = null;
    if (this.finalizationRegistry !== null) {
      allocInfo = this.allocInfos.pop() ?? { wasClosed: false, stack: undefined };
      allocInfo.stack = (new Error()).stack;
      allocInfo.wasClosed = false;
      this.finalizationRegistry.register(bufferRc, allocInfo);
    }
    bufferRc.repurpose(internalBuffer, start, end, allocInfo);
    return bufferRc;
  }

  public stats(): BufferCacheStats {
    return { ...this._stats };
  }
}

